import { makeCygnusApiCall } from "../../../api/api";

export interface InputFieldsControllerProps {
  field: FieldProps;
  handleChange: (fieldId: string, event: any) => void;
  handleBlur: (fieldId: string) => void;
  handleSelect: (fieldId: string) => void;
  onInputChange?: (id: string, value: string, actionMeta?: any) => void;
}

export interface ValidationRule {
  type: "required" | "regex" | "custom";
  message?: string;
  pattern?: string; // for regex validation
  customFn?: (value: any) => boolean; // for custom validation logic
}

export interface Margin {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
}

export interface CascadingOption {
  id: string;
  label: string;
  value: string;
  groupKey?: string; // e.g., stateCode or parent value
}

export interface TextStyle {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strikethrough?: boolean;
  fontSize?: number;
  color?: string;
  width?: string | number;
  margins?: Margin;
}

export type ApiSourceOption = {
  value: string; // e.g., "cch_users"
  label: string; // e.g., "Get from CCH Users"
};

export type NameFormat = "original" | "last_first" | "first_last";
export type CurrencyFormat = "integer" | "decimal";
export type FieldVariant = "default" | "filled" | "outlined" | "underline" | "glass";
export type SectionVariant = "default" | "flat" | "card" | "outlined" | "glass";
export type SectionLayout = "plain" | "banner" | "accent" | "stepped" | "gradient";

export interface FieldProps {
  // Original metadata for Cygnus All Fields
  allfields_category?: string;
  allfields_code?: string;
  code?: string;
  allfields_id?: string | number;
  allfields_key?: string;

  // Required
  type: string;
  id: string;
  label: string;
  value: any;
  selectedOption?: object;

  // Optional
  customLabel?: string;
  systemStatus?: "unmapped";
  placeholder?: string;
  show?: boolean;
  example?: any;
  disabled?: boolean;
  readOnly?: boolean;
  width?: string | number;
  breakLine?: boolean;
  // Page setting
  pageBreak?: boolean;
  pageName?: string;
  // Toggle slider
  toggleOnLabel?: string; // e.g. "Yes" (default)
  toggleOffLabel?: string; // e.g. "No"  (default)
  toggleLabelPlacement?: "sides" | "right" | "none"; // default: "sides"

  // Section setting
  sectionStart?: boolean; // marks the start of a section
  sectionEnd?: boolean; // marks the end of a section
  sectionName?: string; // optional label/header for the section
  sectionStyles?: {
    width?: string;
    backgroundColor?: string;
    borderColor?: string;
    titleColor?: string;
  };

  // For fields where user selects the source API before showing actual dropdown
  apiConfig?: {
    apiKey?: string;
    fetchedOptions?: CascadingOption[];
    isFetching?: boolean;
  };

  selectedApiKey?: string; // Tracks what source was picked

  selected?: boolean;
  headerSize?: string;
  source?: string;
  format?: NameFormat;
  currencyFormat?: CurrencyFormat;
  typeahead?: boolean;
  typeaheadMinChars?: number;
  fetchLimit?: number;
  isFetching?: boolean;
  radiogroup?: string;
  labelPosition?: "top" | "left" | "right" | "bottom";
  options?: CascadingOption[];
  allOptions?: CascadingOption[];
  defaultValue?: string;
  /** True if the current value was set by defaulting logic (not user input) */
  autoValueApplied?: boolean;
  /** True if user clicked Reset; prevents auto defaults from re-applying */
  userCleared?: boolean;
  /** declarative rule: how this field’s value is derived from other fields */
  valueRule?: {
    /** parent reference; keep your existing allfields_code pattern */
    dependsOn?: string; // optional; fallback to field.dependsOn
    /** which parent values activate this rule */
    whenParentValueIn?: any[];
    /** what to do when rule is active */
    setValue?: any;
    /** compute dynamically */
    computeKey?: string; // extend as needed
    computeArgs?: Record<string, any>; // optional params like prefix
    /** behavior when rule is NOT active */
    elseClear?: boolean; // default false
  };
  /**
   * Whether this field’s value is allowed to be auto-overwritten.
   * Default: false -> only auto-set when empty/autoValueApplied/valueRuleApplied.
   */
  valueAuto?: boolean;
  /** internal marker: last update came from valueRule */
  valueRuleApplied?: boolean;
  dependsOn?: string; // field.id it depends on
  dependsOnLabelKey?: string; // optional key from parent to match
  dependsOnVisibilityValue?: any; // the value that triggers "show"
  dependsOnVisibilityValues?: any[]; // NEW: list of values that trigger "show"
  showTimeSelect?: boolean;
  fileLimit?: "single" | "multiple";
  uploading?: boolean;

  // Optional field margins
  margins?: Margin;
  // Optional text styles to apply to the label/input content
  textStyle?: TextStyle;

  // Validation support
  validation?: ValidationRule[];

  // Runtime validation state (controlled by handleChange)
  isValid?: boolean;
  error?: string;

  /** If true, CygnusForm will emit a parent event whenever this field changes */
  emitOnChange?: boolean;
  /** Optional event name to help parent route handling (e.g., "client.lookup") */
  onChangeEventKey?: string;

  emitOnBlur?: boolean; // ← fire when the field blurs (Tab, click away)
  onBlurEventKey?: string; // ← fallback to onChangeEventKey if not set

  emitOnEnter?: boolean; // ← fire when user presses Enter on this field
  onEnterEventKey?: string; // ← fallback to onChangeEventKey if not set
  /** Show a small manual emit button beside the label */
  emitOnButton?: boolean;
  /** Optional visible label for the button */
  emitButtonLabel?: string;
  /** Optional custom event key; fallback to onChangeEventKey if empty */
  emitButtonEventKey?: string;

  defaultToToday?: boolean;
  isDOB?: boolean;
  dateConfig?: {
    displayFormatDate?: string;
    displayFormatDateTime?: string;
    storageFormatDate?: string;
    storageFormatDateTime?: string;
  };
}

export const handleApiDropdownSelection = (
  field: FieldProps,
  selectedValue: string,
  currentFields: FieldProps[],
  setFields: (fields: FieldProps[]) => void
): FieldProps => {
  const payload: Record<string, any> = {
    SelectedApiKey: selectedValue,
    fieldId: field.id,
    fieldKey: field.allfields_key ?? field.id,
    allfields_code: field.allfields_code,
  };

  // ---- Typeahead detection (strict) ----
  const minChars = field.typeaheadMinChars ?? 3;
  const limit = field.fetchLimit ?? 30;
  const rawQuery = typeof field.value === "string" ? field.value.trim() : "";
  const isTypeaheadFetch =
    field.type === "select" && !!field.typeahead && rawQuery.length >= minChars;

  // Only add SearchText/Limit when it's a typeahead request
  if (isTypeaheadFetch) {
    payload["SearchText"] = rawQuery;
    payload["Limit"] = limit;
  }

  // Only set the spinner when it's a typeahead request
  if (isTypeaheadFetch) {
    setFields(
      currentFields.map((f) =>
        f.id === field.id
          ? { ...f, apiConfig: { ...f.apiConfig, isFetching: true } }
          : f
      )
    );
  }

  makeCygnusApiCall({
    type: "POST",
    url: "/dropdown/fetch_options/",
    payload,
    show_message: false,
    customized_success_message: "Dropdown options fetched successfully.",
    customized_failure_message: "Failed to fetch dropdown options.",
    customized_error_message: "Something went wrong while loading options.",
    isblob: false,
    forMDM: true,
  })
    .then((response: any) => {
      if (!response?.success || !Array.isArray(response.data)) return;

      const updatedFields = currentFields.map((f) =>
        f.id === field.id
          ? {
            ...f,
            apiConfig: {
              ...f.apiConfig,
              fetchedOptions: response.data,
              // Only clear spinner if we set it
              ...(isTypeaheadFetch ? { isFetching: false } : {}),
            },
            options: response.data,
          }
          : f
      );

      setFields(updatedFields);
    })
    .catch((err) => {
      console.error("Dropdown fetch failed:", err);

      // Only clear spinner if we set it
      if (isTypeaheadFetch) {
        setFields(
          currentFields.map((f) =>
            f.id === field.id
              ? { ...f, apiConfig: { ...f.apiConfig, isFetching: false } }
              : f
          )
        );
      }
    });

  return { ...field, apiConfig: { ...field.apiConfig } };
};

export function emitAutomation(eventKey: string, payload: any) {
  if (!eventKey) return;
  try {
    window.dispatchEvent(
      new CustomEvent("cygnus.form.emit", { detail: { eventKey, payload } })
    );
  } catch { }
}

const isEmpty = (v: any) =>
  v === null ||
  v === undefined ||
  (typeof v === "string" && v.trim() === "") ||
  (Array.isArray(v) && v.length === 0);

const isBooleanField = (f: FieldProps) =>
  f.type === "checkbox" || f.type === "radio" || f.type === "slider";

const toBool = (v: any): boolean => {
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;
  return false;
};

/**
 * Applies defaultValue only when:
 * - field is visible (checked outside)
 * - value is empty
 * - user didn't explicitly clear it
 */
export const applyDefaultIfEligible = (f: FieldProps): FieldProps => {
  if (f.userCleared) return f;
  if (!isEmpty(f.value)) return f;
  if (isEmpty(f.defaultValue)) return f;

  if (isBooleanField(f)) {
    return { ...f, value: toBool(f.defaultValue), autoValueApplied: true };
  }

  return { ...f, value: f.defaultValue, autoValueApplied: true };
};

/**
 * Clears value ONLY if it was auto-applied.
 * This is what prevents nuking user-entered values when a radio branch hides.
 */
export const clearIfAutoApplied = (f: FieldProps): FieldProps => {
  if (!f.autoValueApplied) return f;

  if (isBooleanField(f)) {
    return { ...f, value: false, autoValueApplied: false };
  }

  if (f.type === "select") {
    return { ...f, value: "", selectedOption: undefined, autoValueApplied: false };
  }

  return { ...f, value: "", autoValueApplied: false };
};

const normalizeVisibilityAtom = (v: any) => {
  // Coerce string booleans to real booleans
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;

  // Keep your current behavior for strings (trim)
  if (typeof v === "string") return v.trim();

  return v;
};

export const shouldFieldBeVisibleForDefaults = (
  field: FieldProps,
  allFields: FieldProps[]
): boolean => {
  if (field.show === false) return false;
  if (!field.dependsOn) return true;

  const expectedValues =
    Array.isArray(field.dependsOnVisibilityValues) &&
    field.dependsOnVisibilityValues.length > 0
      ? field.dependsOnVisibilityValues
      : field.dependsOnVisibilityValue !== undefined &&
        field.dependsOnVisibilityValue !== ""
      ? [field.dependsOnVisibilityValue]
      : undefined;

  if (!expectedValues || expectedValues.length === 0) return true;

  const parent = allFields.find((f) => f.allfields_code === field.dependsOn);
  if (!parent) return false;

  const parentVal = parent.value;

  // normalize parent + expected
  const normalizedExpected = expectedValues.map(normalizeVisibilityAtom);

  if (Array.isArray(parentVal)) {
    return parentVal
      .map(normalizeVisibilityAtom)
      .some((pv) => normalizedExpected.some((ev) => pv === ev));
  }

  const pv = normalizeVisibilityAtom(parentVal);
  return normalizedExpected.some((ev) => pv === ev);
};


/** Safe clear based on field type */
const clearValueForType = (f: FieldProps): Partial<FieldProps> => {
  if (f.type === "checkbox" || f.type === "radio" || f.type === "slider")
    return { value: false };
  if (f.type === "select") return { value: "", selectedOption: undefined };
  if (f.type === "file") return { value: undefined };
  return { value: "" };
};

/**
 * Can we overwrite this field automatically right now?
 * - If valueAuto === true: yes, always (dangerous but sometimes needed)
 * - Otherwise: only if empty OR we previously auto-set it (autoValueApplied/valueRuleApplied)
 * - If userCleared: never resurrect automatically
 */
const canAutoOverwrite = (f: FieldProps) => {
  if (f.valueAuto) return true;
  if (f.userCleared) return false;

  return (
    isEmpty(f.value) ||
    f.autoValueApplied === true ||
    f.valueRuleApplied === true
  );
};

const normalizeRuleAtom = (v: any) => {
  if (v === true || v === false) return v;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    if (t === "true") return true;
    if (t === "false") return false;
    return t;
  }
  return v;
};

const matchesRule = (parentVal: any, expected: any[]) => {
  const p = normalizeRuleAtom(parentVal);
  return expected.some((ev) => normalizeRuleAtom(ev) === p);
};

/**
 * Applies valueRule updates to children whose rule depends on the changed parent.
 *
 * This is triggered from handleChange AFTER the parent value is updated AND
 * AFTER cascading options are applied (so child validity checks are correct).
 *
 * Key behaviors:
 * 1) If rule is active and we can overwrite => set computed/static value
 * 2) Mark valueRuleApplied so future branch switches can safely clear only auto values
 * 3) If rule is inactive and elseClear=true => clear value (but only if we can overwrite)
 */
const applyValueRulesForParentChange = (
  fields: FieldProps[],
  changedParentAllfieldsCode?: string
): FieldProps[] => {
  if (!changedParentAllfieldsCode) return fields;

  return fields.map((f) => {
    const rule = f.valueRule;
    if (!rule) return f;

    // Determine which parent code this rule uses
    const parentCode = rule.dependsOn || f.dependsOn;
    if (!parentCode) return f;

    // Only act if this field depends on the parent that just changed
    if (parentCode !== changedParentAllfieldsCode) return f;

    // Find parent by your existing allfields_code pattern
    const parent = fields.find((x) => x.allfields_code === parentCode);
    if (!parent) return f;

    const parentVal = parent.value;
    const expected = rule.whenParentValueIn ?? [];

    // Rule activation check
    const active =
      expected.length === 0
        ? true
        : Array.isArray(parentVal)
          ? parentVal.some((pv) => matchesRule(pv, expected))
          : matchesRule(parentVal, expected);

    // If rule is NOT active
    if (!active) {
      // Only clear if elseClear is enabled AND we are allowed to overwrite
      if (rule.elseClear && canAutoOverwrite(f)) {
        return {
          ...f,
          ...clearValueForType(f),
          valueRuleApplied: false,
          // optional: clear validation for derived/cleared values
          error: "",
          isValid: undefined,
        };
      }
      return f;
    }

    // Rule IS active: set value if allowed
    if (!canAutoOverwrite(f)) return f;

    let nextValue = rule.setValue;

    if (rule.computeKey) {
      try {
        switch (rule.computeKey) {
          case "copy_parent":
            nextValue = parent.value;
            break;

          case "prefix_parent": {
            const prefix = rule.computeArgs?.prefix ?? "";
            nextValue = `${prefix}${parent.value ?? ""}`;
            break;
          }

          default:
            // unknown computeKey -> do nothing
            return f;
        }
      } catch (e) {
        console.warn("valueRule computeKey failed:", rule.computeKey, f.id, e);
        return f;
      }
    }


    // If compute returns undefined/null, treat as "do nothing"
    if (typeof nextValue === "undefined") return f;

    // Mark as rule-applied so we can safely clear later during branch switches
    return {
      ...f,
      value: nextValue,
      valueRuleApplied: true,
      autoValueApplied: false, // this is a different concept than default-applied
      userCleared: false, // because system just set a value
    };
  });
};


/**
 * Updates the field value on change, without triggering validation.
 *
 * Core responsibilities:
 * 1) Update the changed field value (and mark it as "user-owned")
 * 2) Maintain radio group mutual exclusivity (uncheck others)
 * 3) Cascade dependent option filtering (select/radio children)
 * 4) Reconcile dependent visibility after the change:
 *    - If a dependent field becomes NOT visible: clear only auto-applied values (radio branch cleanup)
 *    - If a dependent field becomes visible: apply defaultValue if eligible
 */
export const handleChange = (
  field_id: string,
  event: any,
  currentFields: FieldProps[],
  setFields: (fields: FieldProps[]) => void
) => {
  let allfields_code: string | undefined;

  // ---------------------------------------------------------------------------
  // Manual emit shortcut (no state change required)
  // ---------------------------------------------------------------------------
  if (event?.manualEmit === true) {
    const f = currentFields.find((x) => x.id === field_id);
    if (f) {
      const eventKey = f.emitButtonEventKey || f.onChangeEventKey || "";
      if (eventKey) {
        const payload = { id: f.id, value: f.value, field: f };
        emitAutomation(eventKey, payload);
      }
    }
    return;
  }

  // ---------------------------------------------------------------------------
  // Reset to API source selection mode before anything else
  // ---------------------------------------------------------------------------
  if (event?.refreshApiSource) {
    const resetFields = currentFields.map((f) =>
      f.id === field_id ? { ...f, value: "" } : f
    );
    setFields(resetFields);

    const targetField = currentFields.find((f) => f.id === field_id);
    if (targetField) {
      handleApiDropdownSelection(
        targetField,
        targetField.apiConfig?.apiKey || "",
        currentFields,
        setFields
      );
    }
    return;
  }

  // ---------------------------------------------------------------------------
  // Step 1: Update the changed field value
  // - Also enforce radio-group: uncheck other radios in same group
  // - Track ownership:
  //    autoValueApplied=false -> user touched it (so defaults shouldn't "own" it anymore)
  //    userCleared=true if user explicitly cleared the value
  // ---------------------------------------------------------------------------
  const updatedFields = currentFields.map((field) => {
    // --- Radio group exclusivity ---
    // If user checks one radio, all other radios in that group must be false.
    if (field.type === "radio" && field.radiogroup) {
      const target = currentFields.find((f) => f.id === field_id);
      if (
        target &&
        target.radiogroup === field.radiogroup &&
        field.id !== field_id
      ) {
        // This is a system-driven uncheck, not a user "clear".
        // So: userCleared stays false.
        return {
          ...field,
          value: false,
          autoValueApplied: false,
          userCleared: false,
        };
      }
    }

    // Skip all fields except the one that triggered the change
    if (field.id !== field_id) return field;

    allfields_code = field.allfields_code;

    // Determine new value based on field type
    let newValue: any;
    switch (field.type) {
      case "checkbox":
      case "slider":
      case "radio":
        newValue = event.target.checked;
        break;
      case "select":
        newValue = event?.value;
        break;
      case "file":
        newValue = event?.[0];
        break;
      case "date":
        newValue = event;
        break;
      default:
        newValue = event.target.value;
        break;
    }

    // If user cleared the field, we should NOT resurrect defaults later.
    const wasCleared =
      newValue === "" ||
      newValue === null ||
      typeof newValue === "undefined" ||
      (typeof newValue === "string" && newValue.trim() === "");

    return {
      ...field,
      value: newValue,

      // User changed it -> system should not treat it as auto-owned anymore
      autoValueApplied: false,
      valueRuleApplied: false,

      // if user cleared, block auto-defaulting from re-filling it
      userCleared: wasCleared,

      ...(field.type === "select" && typeof event === "object"
        ? { selectedOption: event }
        : {}),
    };
  });

  // ---------------------------------------------------------------------------
  // Step 2: Cascade option filtering for children
  // - Only applies for children whose dependsOn === changed parent's allfields_code
  // - Only applies to select/radio children
  // ---------------------------------------------------------------------------
  const changedField = updatedFields.find((f) => f.id === field_id);
  const newValue = changedField?.value;

  const normalize = (v: unknown) =>
    v === null || v === undefined ? "" : String(v).trim().toLowerCase();

  const cascadedFields = updatedFields.map((field) => {
    const isChildOfChanged =
      field.dependsOn && field.dependsOn === allfields_code;

    const isSelectLike = field.type === "select" || field.type === "radio";
    if (!isChildOfChanged || !isSelectLike) return field;

    const sourceAllOptions =
      (field.allOptions?.length
        ? field.allOptions
        : currentFields.find((f) => f.id === field.id)?.options) || [];

    if (!Array.isArray(sourceAllOptions) || sourceAllOptions.length === 0) {
      return field;
    }

    const hasUsableGroupKeys = sourceAllOptions.some(
      (o: any) =>
        o &&
        Object.prototype.hasOwnProperty.call(o, "groupKey") &&
        normalize(o.groupKey) !== ""
    );

    // No group keys -> do not filter
    if (!hasUsableGroupKeys) {
      return {
        ...field,
        options: sourceAllOptions,
        allOptions: sourceAllOptions,
      };
    }

    const parentKeys = Array.isArray(newValue)
      ? newValue.map(normalize)
      : [normalize(newValue)];

    const filteredOptions = sourceAllOptions.filter((opt: any) =>
      parentKeys.includes(normalize(opt?.groupKey))
    );

    const nextOptions =
      filteredOptions.length > 0 ? filteredOptions : sourceAllOptions;

    const keepValue = nextOptions.some((o: any) => o?.value === field.value);

    return {
      ...field,
      options: nextOptions,
      allOptions: sourceAllOptions,
      value: keepValue ? field.value : "",
    };
  });

  // ---------------------------------------------------------------------------
  // Step 3: Reconcile dependent visibility after the change
  //
  // This fixes the "radio branch children" bug:
  // - When a branch becomes inactive (not visible), its child values should not linger
  // - But we must not wipe user-entered values accidentally
  //
  // Rules:
  // - If NOT visible -> clear only values that were auto-applied (autoValueApplied===true)
  // - If visible -> apply default only if eligible (empty + has defaultValue + not userCleared)
  // ---------------------------------------------------------------------------
  // 1) Apply valueRules for children of the changed parent (parent-driven derivations)
  const afterRules = applyValueRulesForParentChange(cascadedFields, allfields_code);

  // 2) Reconcile dependent visibility + defaults
  const reconciled = afterRules.map((f) => {
    const visible = shouldFieldBeVisibleForDefaults(f, afterRules);

    if (!visible) {
      // Not visible: clear only auto-applied values so stale defaults don't persist.
      // Also wipe validation UI noise for hidden branch fields.
      return clearIfAutoApplied({
        ...f,
        error: "",
        isValid: undefined,
      });
    }

    // Visible: apply default only if eligible (your helper should enforce:
    // - empty value
    // - has defaultValue
    // - userCleared !== true
    // - autoValueApplied !== false by user touch)
    return applyDefaultIfEligible(f);
  });

  setFields(reconciled);
};

/**
 * Validates the field when it loses focus (onBlur).
 *
 * @param field_id - ID of the field that lost focus
 * @param currentFields - Current field state
 * @param setFields - Setter to update the field list
 */
export const handleBlur = (
  field_id: string,
  currentFields: FieldProps[],
  setFields: (fields: FieldProps[]) => void
) => {
  const updatedFields = currentFields.map((field) => {
    if (field.id !== field_id) return field;

    const value = field.value;
    let isValid = true;
    let error = "";

    const isRequired =
    field.validation?.some((rule) => rule.type === "required") ?? false;

    const isEmptyValue =
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "") ||
      (Array.isArray(value) && value.length === 0);

    if (!isRequired && isEmptyValue) {
      return {
        ...field,
        isValid: true,
        error: "",
      };
    }

    if (field.validation && field.validation.length > 0) {
      for (const rule of field.validation) {
        if (rule.type === "required" && !value) {
          isValid = false;
          error = rule.message ?? "This field is required.";
          break;
        }

        if (rule.type === "regex" && rule.pattern) {
          try {
            const pattern = rule.pattern.trim().replace(/^\/|\/$/g, "");
            const regex = new RegExp(pattern);
            if (!regex.test(value)) {
              isValid = false;
              error = rule.message ?? "Invalid format.";
              break;
            }
          } catch (err) {
            console.warn("Invalid regex pattern:", rule.pattern);
          }
        }

        if (rule.type === "custom" && rule.customFn && !rule.customFn(value)) {
          isValid = false;
          error = rule.message ?? "Invalid input.";
          break;
        }
      }
    }

    return {
      ...field,
      isValid,
      error,
    };
  });

  setFields([...updatedFields]);
};

/**
 * Checks whether a form field includes a "required" validation rule.
 * This is used primarily for rendering a visual indicator (e.g. red asterisk)
 * on fields that are required, before any interaction or validation occurs.
 *
 * @param field - The field object containing validation metadata.
 * @returns True if the field has a "required" validation rule; otherwise, false.
 */
export const hasRequiredValidation = (field: FieldProps): boolean => {
  return field.validation?.some((rule) => rule.type === "required") ?? false;
};

export const hasValue = (field: FieldProps): boolean => {
  return field.value ? true : false;
};

/**
 * Computes the inline container style for a form field based on its layout metadata.
 * It supports custom width (as number or string) and margins, and avoids full-width
 * overrides unless explicitly set.
 *
 * @param {FieldProps} field - The field definition containing width and margins
 * @returns {React.CSSProperties} The computed style object to apply to the field wrapper
 */
export const getFieldContainerStyle = (
  field: FieldProps
): React.CSSProperties => {
  const { width, margins = { top: 5, right: 5, bottom: 5, left: 5 } } = field;

  const resolvedWidth =
    typeof width === "number" ? `${width}%` : width ?? "auto";

  return {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: resolvedWidth,
    margin: `${margins.top}px ${margins.right}px ${margins.bottom}px ${margins.left}px`,
    boxSizing: "border-box",
    cursor: "pointing",
  };
};

/**
 * Generates reusable inline styles for text labels based on textStyle config.
 *
 * @param textStyle - The styling options (bold, italic, etc.)
 * @returns CSSProperties for label styling
 */
export const getLabelTextStyle = (
  textStyle?: FieldProps["textStyle"]
): React.CSSProperties => {
  if (!textStyle) return {};

  return {
    fontWeight: textStyle.bold ? "bold" : undefined,
    fontStyle: textStyle.italic ? "italic" : undefined,
    fontSize: textStyle.fontSize ? `${textStyle.fontSize}px` : undefined,
    color: textStyle.color,
    lineHeight: "14px",
    width: textStyle.width ? `${textStyle.width}%` : "auto",
    margin: textStyle.margins
      ? `${textStyle.margins.top ?? 0}px ${textStyle.margins.right ?? 0}px ${textStyle.margins.bottom ?? 0
      }px ${textStyle.margins.left ?? 0}px`
      : undefined,
    whiteSpace: "normal",
    cursor: "pointing",
    textDecoration:
      [
        textStyle.underline ? "underline" : "",
        textStyle.strikethrough ? "line-through" : "",
      ]
        .filter(Boolean)
        .join(" ") || undefined,
  };
};

/**
 * Applies a display name formatting rule to a raw name string.
 *
 * Behavior:
 * - If `fmt` is not provided or is `"original"`, the raw string is returned unchanged.
 * - Input may be either:
 *      • "Last, First"
 *      • "First Last"
 *      • Single name
 * - Safely parses name parts with trimming + empty checks.
 * - Reconstructs formatted name using:
 *      - "last_first"  → "Last, First"
 *      - "first_last"  → "First Last"
 *
 * Note:
 * Without structured name metadata, multi-word or middle names cannot be
 * perfectly parsed. This function preserves all name parts and ensures stable,
 * reversible formatting wherever possible.
 *
 * @param raw - The unformatted input name.
 * @param fmt - The formatting rule to apply.
 */
export const applyNameFormat = (raw: string, fmt?: NameFormat) => {
  if (!raw || !fmt || fmt === "original") return raw;

  const trimmed = raw.trim();
  if (!trimmed) return raw;

  let first = "";
  let last = "";

  if (trimmed.includes(",")) {
    const parts = trimmed.split(",").map(p => p.trim()).filter(Boolean);

    last = parts[0] ?? "";
    first = parts.slice(1).join(" ") ?? "";

  } else {
    const parts = trimmed.split(" ").filter(Boolean);

    if (parts.length === 1) {
      first = parts[0];
      last = "";
    } else {
      first = parts[0];
      last = parts.slice(1).join(" ");
    }
  }

  switch (fmt) {
    case "last_first": {
      if (!last) return first;
      if (!first) return last;
      return `${last}, ${first}`.trim();
    }

    case "first_last": {
      return [first, last].filter(Boolean).join(" ").trim();
    }

    default:
      return raw;
  }
};

/**
 * validateField
 *
 * Runs validation rules for a single field (required, regex, custom).
 * Stops at the first failing rule and sets the corresponding error message.
 *
 * Returns the updated field with:
 * - isValid : validation status
 * - error   : message to display if validation fails
 *
 * Used during blur validation and on-demand form validation (Next/Submit).
 */
export const validateField = (field: FieldProps): FieldProps => {
  const value = field.value;
  let isValid = true;
  let error = "";

  const isRequired =
    field.validation?.some((rule) => rule.type === "required") ?? false;

  const isEmptyValue =
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);

  if (!isRequired && isEmptyValue) {
    return {
      ...field,
      isValid: true,
      error: "",
    };
  }

  if (field.validation && field.validation.length > 0) {
    for (const rule of field.validation) {
      if (rule.type === "required" && !value) {
        isValid = false;
        error = rule.message ?? "This field is required.";
        break;
      }

      if (rule.type === "regex" && rule.pattern) {
        try {
          const pattern = rule.pattern.trim().replace(/^\/|\/$/g, "");
          const regex = new RegExp(pattern);
          if (!regex.test(value)) {
            isValid = false;
            error = rule.message ?? "Invalid format.";
            break;
          }
        } catch (err) {
          console.warn("Invalid regex pattern:", rule.pattern);
        }
      }

      if (rule.type === "custom" && rule.customFn && !rule.customFn(value)) {
        isValid = false;
        error = rule.message ?? "Invalid input.";
        break;
      }
    }
  }

  return {
    ...field,
    isValid,
    error,
  };
};
